import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DotsIcon } from 'applicaiton/assets/dots-vertical.svg';
import { ReactComponent as EditIcon } from 'applicaiton/assets/edit-pencil.svg';
import { ReactComponent as DownloadIcon } from 'applicaiton/assets/download.svg';
import { ReactComponent as DeleteIcon } from 'applicaiton/assets/delete.svg';
import useOnClickOutside from 'common/hooks/useClickOutside';
import { ExpandedActionsProps } from './models';
import { Wrapper, ActionsListStyled } from './styles';

/**
 * The `ExpandedActions` component is a React functional component that renders a button with three vertical dots.
 * When the button is clicked, it toggles the visibility of an actions list containing three buttons for editing,
 * downloading, and deleting a document. This component utilizes the `useTranslation` hook from `react-i18next` for
 * internationalization support.
 *
 * @param {ExpandedActionsProps} props - An object containing the following properties:
 *   - handleEdit: A function to be called when the edit button is clicked.
 *   - handleDeleteDocument: A function to be called when the delete button is clicked.
 *   - handleDownloadDocument: A function to be called when the download button is clicked.
 *
 * @returns {JSX.Element} The JSX element representing the `ExpandedActions` component.
 */

const ExpandedActions: FC<ExpandedActionsProps> = ({
  handleEdit,
  handleDeleteDocument,
  handleDownloadDocument,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useOnClickOutside(ref, () => setIsActive(false));

  return (
    <Wrapper ref={ref}>
      <button type="button" onClick={() => setIsActive((prev) => !prev)}>
        <DotsIcon />
      </button>
      {isActive && (
        <ActionsListStyled>
          <button onClick={handleEdit}>
            <EditIcon />
            {t('edit')}
          </button>
          <button onClick={handleDownloadDocument}>
            <DownloadIcon />
            {t('download')}
          </button>
          <button onClick={handleDeleteDocument}>
            <DeleteIcon />
            {t('delete')}
          </button>
        </ActionsListStyled>
      )}
    </Wrapper>
  );
};

export default ExpandedActions;
